import { useMemo, type ReactNode } from 'react'
import clsx from 'clsx'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import type {
  ReturnsTrackingInfoResponseLogisticsInfo,
  ReturnsTrackingInfoResponseRefundInfo
} from '@itsrever/rever-api'
import {
  ReturnsTrackingInfoResponseLogisticsInfoLabelStatusEnum,
  ReturnsTrackingInfoResponseLogisticsInfoReturnMethodEnum,
  ReturnsTrackingInfoResponseRefundInfoPaymentMethodEnum,
  ReturnsTrackingInfoResponseRefundInfoStatusEnum,
  ReturnsTrackingInfoResponseRefundInfoTimingEnum,
  ReturnsTrackingInfoResponseStatusEnum
} from '@itsrever/rever-api'
import { getDate } from '@/utils'
import { useAppSelector } from '@/redux/hooks'
import { TruckIcon } from '@/ui/icons/truck'
import { SearchIcon } from '@/ui/icons/search'
import { HandCashIcon } from '@/ui/icons/hand-cash'
import { CubeIcon } from '@/ui/icons/cube'
import { ShieldSearchIcon } from '@/ui/icons/shield-search'
import { XMarkIcon } from '@/ui/icons/xmark'
import { CheckIcon } from '@/ui/icons/check'

type StepState = 'completed' | 'current' | 'upcoming' | 'terminal'
type StepId =
  | 'authorization'
  | 'initiated'
  | 'in_transit'
  | 'quality_review'
  | 'refund_issued'
  | 'cancelled'
  | 'not_authorized'

interface TimelineStep {
  id: StepId
  state: StepState
  title: string
  description: ReactNode
  icon: ReactNode
}

const iconsById: Record<StepId, ReactNode> = {
  authorization: <ShieldSearchIcon className="size-5" strokeWidth={1.5} />,
  initiated: <CubeIcon className="size-5" strokeWidth={1.5} />,
  in_transit: <TruckIcon className="size-5" strokeWidth={1.5} />,
  quality_review: <SearchIcon className="size-4" strokeWidth={1.5} />,
  refund_issued: <HandCashIcon className="size-5" strokeWidth={1.5} />,
  cancelled: <XMarkIcon className="size-5" strokeWidth={1.5} />,
  not_authorized: <XMarkIcon className="size-5" strokeWidth={1.5} />
}

const isInStoreReturn = (
  logisticsInfo?: ReturnsTrackingInfoResponseLogisticsInfo
): boolean =>
  logisticsInfo?.return_method ===
  ReturnsTrackingInfoResponseLogisticsInfoReturnMethodEnum.InStore

const isRefundIssued = (
  refundInfo?: ReturnsTrackingInfoResponseRefundInfo
): boolean =>
  refundInfo?.status ===
    ReturnsTrackingInfoResponseRefundInfoStatusEnum.FullyRefunded ||
  refundInfo?.status ===
    ReturnsTrackingInfoResponseRefundInfoStatusEnum.PartiallyRefunded

// -- Which steps appear, and how far the return has progressed ----------------

const getStepOrder = (
  timing: ReturnsTrackingInfoResponseRefundInfoTimingEnum
): StepId[] => {
  switch (timing) {
    case ReturnsTrackingInfoResponseRefundInfoTimingEnum.OnStartReturnProcess:
    case ReturnsTrackingInfoResponseRefundInfoTimingEnum.OnItemSent:
      return ['initiated', 'refund_issued', 'in_transit', 'quality_review']
    default:
      return ['initiated', 'in_transit', 'quality_review', 'refund_issued']
  }
}

// How far the physical return has progressed, independent of the refund.
const progressByStatus: Partial<
  Record<ReturnsTrackingInfoResponseStatusEnum, number>
> = {
  [ReturnsTrackingInfoResponseStatusEnum.Started]: 0,
  [ReturnsTrackingInfoResponseStatusEnum.InTransit]: 1,
  [ReturnsTrackingInfoResponseStatusEnum.InWarehouse]: 2,
  [ReturnsTrackingInfoResponseStatusEnum.Reviewed]: 3
}

// Where each physical step sits on that progression (refund is handled apart).
const physicalStepRank: Record<
  Exclude<
    StepId,
    'authorization' | 'refund_issued' | 'cancelled' | 'not_authorized'
  >,
  number
> = {
  initiated: 0,
  in_transit: 1,
  quality_review: 2
}

interface LocalizedCopy {
  key: string
  options?: Record<string, unknown>
}

interface StepCopy {
  title: LocalizedCopy
  description: LocalizedCopy
}

interface CopyContext {
  state: StepState
  language: string
  labelStatus?: ReturnsTrackingInfoResponseLogisticsInfoLabelStatusEnum
  refundInfo?: ReturnsTrackingInfoResponseRefundInfo
  refundTiming: ReturnsTrackingInfoResponseRefundInfoTimingEnum
  inStore: boolean
}

const progressKey = (name: string): string => `tracking_progress.${name}`

const inStoreVariant = (name: string, inStore: boolean): string =>
  progressKey(inStore ? `${name}_in_store` : name)

// Timing/method drive copy keys by interpolation, but the API can send
// unspecified/not-necessary values that have no translation. Normalize timing to
// a milestone that always has copy, and only key on a payment method we localize.
const resolveRefundTiming = (
  timing?: ReturnsTrackingInfoResponseRefundInfoTimingEnum
): ReturnsTrackingInfoResponseRefundInfoTimingEnum =>
  timing &&
  timing !==
    ReturnsTrackingInfoResponseRefundInfoTimingEnum.RefundTimingUnspecified
    ? timing
    : ReturnsTrackingInfoResponseRefundInfoTimingEnum.OnItemVerified

const localizedPaymentMethods: ReadonlySet<string> = new Set([
  ReturnsTrackingInfoResponseRefundInfoPaymentMethodEnum.Original,
  ReturnsTrackingInfoResponseRefundInfoPaymentMethodEnum.BankTransfer,
  ReturnsTrackingInfoResponseRefundInfoPaymentMethodEnum.PromoCode,
  ReturnsTrackingInfoResponseRefundInfoPaymentMethodEnum.GiftCard
])

const resolveRefundCopy = (ctx: CopyContext): StepCopy => {
  const { refundInfo, language, refundTiming } = ctx
  const paymentMethod = refundInfo?.payment_method
  const method =
    paymentMethod && localizedPaymentMethods.has(paymentMethod)
      ? paymentMethod
      : null

  if (isRefundIssued(refundInfo)) {
    const date = getDate(refundInfo?.refunded_at ?? '', language)
    return {
      title: date
        ? { key: progressKey('step_refund_issued_done'), options: { date } }
        : { key: progressKey('step_refund_issued') },
      description: {
        key: method
          ? `tracking_refund.issued_${method}`
          : 'tracking_refund.issued_generic'
      }
    }
  }

  return {
    title: { key: progressKey('step_refund_issued') },
    description: {
      key: method
        ? `tracking_refund.pending_refund_${method}_${refundTiming}`
        : 'tracking_refund.pending_refund_generic'
    }
  }
}

const resolveStepCopy = (id: StepId, ctx: CopyContext): StepCopy => {
  const { state, inStore } = ctx
  const completed = state === 'completed'
  const upcoming = state === 'upcoming'

  switch (id) {
    case 'authorization':
      return {
        title: { key: progressKey('step_authorization') },
        description: { key: progressKey('authorization') }
      }

    case 'initiated': {
      const labelStatus =
        ctx.labelStatus ??
        ReturnsTrackingInfoResponseLogisticsInfoLabelStatusEnum.Available
      return {
        title: { key: progressKey('step_initiated') },
        description: { key: progressKey(`return_initiated_${labelStatus}`) }
      }
    }

    case 'in_transit': {
      const descriptionName = completed
        ? 'in_warehouse'
        : upcoming
          ? 'in_transit_upcoming'
          : 'in_transit'
      return {
        title: {
          key: inStoreVariant(
            completed ? 'step_in_transit_done' : 'step_in_transit',
            inStore
          )
        },
        description: { key: inStoreVariant(descriptionName, inStore) }
      }
    }

    case 'quality_review': {
      const descriptionName = completed
        ? 'quality_reviewed'
        : upcoming
          ? 'quality_review_upcoming'
          : 'quality_review'
      return {
        title: {
          key: progressKey(
            completed ? 'step_quality_review_done' : 'step_quality_review'
          )
        },
        description: { key: progressKey(descriptionName) }
      }
    }

    case 'refund_issued':
      return resolveRefundCopy(ctx)

    // cancelled / not_authorized are terminal single-step states (see getSteps).
    default:
      return {
        title: { key: progressKey(`step_${id}`) },
        description: { key: progressKey(id) }
      }
  }
}

// The headline promotes the refund once issued and otherwise mirrors the
// current step, using the dedicated `title_*` copies.
const resolveHeadline = (
  current: TimelineStep | undefined,
  refundInfo: ReturnsTrackingInfoResponseRefundInfo | undefined,
  inStore: boolean,
  language: string
): LocalizedCopy => {
  if (current?.state !== 'terminal' && isRefundIssued(refundInfo)) {
    const date = getDate(refundInfo?.refunded_at ?? '', language)
    return date
      ? { key: progressKey('step_refund_issued_done'), options: { date } }
      : { key: progressKey('step_refund_issued') }
  }
  if (current?.id === 'in_transit' && inStore) {
    return { key: progressKey('title_in_transit_in_store') }
  }
  return { key: progressKey(`title_${current?.id ?? 'done'}`) }
}

const terminalStep = (
  t: TFunction,
  id: 'cancelled' | 'not_authorized',
  descriptionOptions?: Record<string, unknown>
): TimelineStep => ({
  id,
  state: 'terminal',
  title: t(progressKey(`step_${id}`)),
  description: t(progressKey(id), descriptionOptions ?? {}),
  icon: iconsById[id]
})

const getSteps = (
  t: TFunction,
  language: string,
  status?: ReturnsTrackingInfoResponseStatusEnum,
  refundInfo?: ReturnsTrackingInfoResponseRefundInfo,
  logisticsInfo?: ReturnsTrackingInfoResponseLogisticsInfo,
  ecommerceName?: string
): TimelineStep[] => {
  if (status === ReturnsTrackingInfoResponseStatusEnum.Canceled) {
    return [terminalStep(t, 'cancelled', { ecommerceName })]
  }
  if (status === ReturnsTrackingInfoResponseStatusEnum.NotAuthorized) {
    return [terminalStep(t, 'not_authorized')]
  }

  const timing = resolveRefundTiming(refundInfo?.timing)
  const refundNotNecessary =
    refundInfo?.status ===
    ReturnsTrackingInfoResponseRefundInfoStatusEnum.RefundNotNecessary
  const noReturnMethod =
    logisticsInfo?.return_method ===
    ReturnsTrackingInfoResponseLogisticsInfoReturnMethodEnum.NoReturnMethod
  const authorizationPending =
    status === ReturnsTrackingInfoResponseStatusEnum.AuthorizationPending

  const ids: StepId[] = [
    ...(authorizationPending ? (['authorization'] as const) : []),
    ...getStepOrder(timing)
      .filter((id) => !(refundNotNecessary && id === 'refund_issued'))
      .filter((id) => !(noReturnMethod && id === 'in_transit'))
  ]

  const progress = (status && progressByStatus[status]) ?? 0
  const refundIdx = ids.indexOf('refund_issued')
  const inTransitIdx = ids.indexOf('in_transit')
  const refundIssued = isRefundIssued(refundInfo)
  // ON_START/ON_ITEM_SENT place the refund before the item ships. Once such a
  // refund is issued but the item hasn't started moving, the refund is the step
  // to highlight, with initiation already behind us.
  const refundFrontLoaded =
    refundIdx !== -1 && (inTransitIdx === -1 || refundIdx < inTransitIdx)
  const refundIsCurrent =
    refundIssued && refundFrontLoaded && progress === 0 && !authorizationPending

  const isDone = (id: StepId): boolean => {
    // Authorization only shows while pending, so it is never completed here.
    if (id === 'authorization' || id === 'cancelled' || id === 'not_authorized')
      return false
    if (id === 'refund_issued') return refundIssued
    if (id === 'initiated') return progress > 0 || refundIsCurrent
    return progress > physicalStepRank[id]
  }

  // The current step is the first one not yet done; a front-loaded refund that
  // has already been issued instead claims the highlight (see refundIsCurrent).
  let currentIndex = ids.findIndex((id) => !isDone(id))
  if (refundIsCurrent) currentIndex = refundIdx
  const inStore = isInStoreReturn(logisticsInfo)

  return ids.map((id, index) => {
    const state: StepState =
      index === currentIndex ? 'current' : isDone(id) ? 'completed' : 'upcoming'

    const copy = resolveStepCopy(id, {
      state,
      language,
      labelStatus: logisticsInfo?.label_status,
      refundInfo,
      refundTiming: timing,
      inStore
    })

    return {
      id,
      state,
      title: t(copy.title.key, copy.title.options ?? {}),
      description: t(copy.description.key, copy.description.options ?? {}),
      icon: iconsById[id]
    }
  })
}

interface ReturnProgressProps {
  status?: ReturnsTrackingInfoResponseStatusEnum
  refundInfo?: ReturnsTrackingInfoResponseRefundInfo
  logisticsInfo?: ReturnsTrackingInfoResponseLogisticsInfo
}

const useSteps = (
  { status, refundInfo, logisticsInfo }: ReturnProgressProps,
  ecommerceName?: string
): TimelineStep[] => {
  const { t, i18n } = useTranslation()
  return useMemo(
    () =>
      getSteps(
        t,
        i18n.language,
        status,
        refundInfo,
        logisticsInfo,
        ecommerceName
      ),
    [t, i18n.language, status, refundInfo, logisticsInfo, ecommerceName]
  )
}

export const ReturnProgress = ({
  status,
  refundInfo,
  logisticsInfo
}: ReturnProgressProps) => {
  const { t, i18n } = useTranslation()

  const ecommerceName = useAppSelector(
    (store) => store.settingsApi.settings.response.branding?.title
  )

  const steps = useSteps({ status, refundInfo, logisticsInfo }, ecommerceName)

  const current = steps.find(
    (step) => step.state === 'current' || step.state === 'terminal'
  )

  const headline = resolveHeadline(
    current,
    refundInfo,
    isInStoreReturn(logisticsInfo),
    i18n.language
  )

  return (
    <div>
      <h2 id="return-progress-title" className="pb-5 text-2xl font-semibold">
        {t(headline.key, headline.options ?? {})}
      </h2>
      <ol className="relative -ml-1">
        {steps.map((step, index) => {
          const isLast = index === steps.length - 1

          return (
            <li
              key={step.id}
              aria-current={step.state === 'current' ? 'step' : undefined}
              className="relative flex gap-2.5 pb-3 last:pb-0"
            >
              {!isLast || step.state === 'terminal' ? (
                <span
                  aria-hidden
                  className="absolute left-3 h-full w-px -translate-x-1/2 border-l border-dashed border-neutral-300"
                />
              ) : null}
              <TimelineMarker state={step.state} icon={step.icon} />
              <div className="pt-0.5">
                <p
                  className={clsx('text-sm font-medium', {
                    'text-blue-600': step.state === 'current',
                    'text-neutral-900': step.state === 'completed',
                    'text-neutral-500': step.state === 'upcoming',
                    'text-red-600': step.state === 'terminal'
                  })}
                >
                  {step.title}
                  <span className="sr-only">
                    {' '}
                    ({t(`tracking_progress.step_status_${step.state}`)})
                  </span>
                </p>
                <p className="mt-1 text-xs font-normal text-neutral-500">
                  {step.description}
                </p>
              </div>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

interface TimelineMarkerProps {
  state: StepState
  icon?: ReactNode
}

const TimelineMarker = ({ state, icon }: TimelineMarkerProps) => {
  if (state === 'current' || state === 'terminal') {
    return (
      <span
        aria-hidden
        className={clsx(
          'relative z-10 flex size-6 shrink-0 items-center justify-center rounded-full bg-white',
          state === 'terminal' ? 'text-red-600' : 'text-blue-600'
        )}
      >
        {icon}
      </span>
    )
  }

  if (state === 'completed') {
    return (
      <span
        aria-hidden
        className="relative z-10 flex size-6 shrink-0 items-center justify-center rounded-full bg-white text-neutral-900"
      >
        <CheckIcon className="size-4" strokeWidth={2} />
      </span>
    )
  }

  return (
    <span
      aria-hidden
      className="relative z-10 flex size-6 shrink-0 items-center justify-center bg-white"
    >
      <span className="size-1.5 rounded-full bg-neutral-300" />
    </span>
  )
}
